'use client';

import { useState, useEffect } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import Image from 'next/image';

import { Badge } from '@mantine/core';

import { Icon } from '@/elements/icon/icon.component';
import {
  Container,
  ModuleWrap,
  Module,
  Title,
  Number,
  Description,
  Lecturer,
  LecturerName,
  Features,
  EventTime,
  Time,
  Tags,
  PortraitWrap,
  TitleWrap,
  Icons,
} from './modules.elements';

import { normalizeModuleLecturerEntries } from '@/helpers/module-lecturers.helpers';
import { colors } from '@/styles/theme';
import {
  IconArrowNarrowRight,
  IconClockBolt,
  IconNotes,
  IconAntennaBars1,
  IconAntennaBars2,
  IconAntennaBars3,
  IconAntennaBars4,
  IconAntennaBars5,
} from '@tabler/icons-react';

const videoQualityIndicators = (quality) => {
  const icons = {
    1: <IconAntennaBars1 size={22} color="pink" />,
    2: <IconAntennaBars2 size={22} color="red" />,
    3: <IconAntennaBars3 size={22} color="orange" />,
    4: <IconAntennaBars4 size={22} color="green" />,
    5: <IconAntennaBars5 size={22} color="teal" />,
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        height: 22,
        gap: 2,
        fontSize: '0.9rem',
      }}
    >
      <IconAntennaBars5
        size={22}
        style={{ position: 'absolute', zIndex: -1, bottom: 2, right: 0 }}
        color={colors.brown + 70}
      />
      Kvaliteta snimke
      <div style={{ marginBottom: 2 }}>{icons[quality]}</div>
    </div>
  );
};

function findNextLectureIndex(modules, currentTime) {
  if (modules.every((module) => module.lectureStartDateTime === null)) {
    return 0;
  }

  let nearestIndex = -1;
  let nearestTime = Infinity;

  for (let i = 0; i < modules.length; i++) {
    if (modules[i].lectureStartDateTime === null) continue;

    const lectureTime = new Date(modules[i].lectureStartDateTime);
    if (lectureTime > currentTime && lectureTime < nearestTime) {
      nearestIndex = i;
      nearestTime = lectureTime;
    }
  }

  return nearestIndex;
}

const Modules = ({ modules, bonusModules, myEducation, isCompact = false, type }) => {
  const router = useRouter();
  const pathname = usePathname();
  const [activeModuleIndex, setActiveModuleIndex] = useState(null);

  useEffect(() => {
    const currentTime = new Date(); // Get current time once
    const index = modules.length - 1 - findNextLectureIndex(modules, currentTime);
    setActiveModuleIndex(index);
  }, [modules]);

  const handleModuleClick = (standaloneEducation, slug) => {
    if (myEducation) {
      router.push(`${pathname}/${slug}${type === 'predavac' ? '?type=predavac' : ''}`);
    } else if (standaloneEducation) {
      window.open(
        `${process.env.SITE_URL}/${standaloneEducation.slug}${type === 'predavac' ? '?type=predavac' : ''}`,
        '_blank'
      );
    }
  };

  return (
    <Container
      active={activeModuleIndex}
      bulletSize={48}
      lineWidth={4}
      color={colors.blue}
      reverseActive
    >
      {modules
        .sort((a, b) => {
          // Check if modules are bonus modules
          const aIsBonus = bonusModules?.find((bonus) => bonus.id === a.id);
          const bIsBonus = bonusModules?.find((bonus) => bonus.id === b.id);

          // Bonus modules always go to the end
          if (aIsBonus && !bIsBonus) return 1;
          if (!aIsBonus && bIsBonus) return -1;

          // For non-bonus modules, sort by date
          if (!a.lectureStartDateTime) return 1;
          if (!b.lectureStartDateTime) return -1;
          return new Date(a.lectureStartDateTime) - new Date(b.lectureStartDateTime);
        })
        .map(
          (
            {
              id,
              title,
              description,
              lecturers,
              lectureStartDateTime,
              tags,
              video: { recordingId, videoQuality },
              materials: { summary, documents },
              standaloneEducation,
              slug,
            },
            i
          ) => (
            <ModuleWrap
              key={id}
              bullet={<Number>{i + 1}</Number>}
              title={
                <TitleWrap>
                  <Title>{title}</Title>
                  {(!!standaloneEducation || !!myEducation) && modules.length > 1 && !isCompact ? (
                    <div className="arrow">
                      <Icon color="blue">
                        <IconArrowNarrowRight />
                      </Icon>
                    </div>
                  ) : null}
                </TitleWrap>
              }
              lineVariant="dashed"
              $hasLink={
                (!!standaloneEducation || !!myEducation) && modules.length > 1 && !isCompact
              }
              $myEducation={!!myEducation}
              onClick={!isCompact ? () => handleModuleClick(standaloneEducation, slug) : null}
            >
              <Module $compact={isCompact}>
                {normalizeModuleLecturerEntries(lecturers).map((entry) => {
                  const lecturer = entry.lecturer;
                  const profileImage = lecturer?.profileImage;
                  const imageUrl = profileImage?.sizes?.smallSquare?.url;
                  const imageAlt = profileImage?.alt || `${lecturer.firstName} ${lecturer.lastName}`;

                  return (
                    <Lecturer key={entry.id || lecturer.id}>
                      {imageUrl ? (
                        <PortraitWrap>
                          <Image src={imageUrl} width={20} height={20} alt={imageAlt} />
                        </PortraitWrap>
                      ) : null}
                      <LecturerName>{`${lecturer.firstName} ${lecturer.lastName}`}</LecturerName>
                    </Lecturer>
                  );
                })}
                <Features>
                  <Icons>
                    {lectureStartDateTime && new Date(lectureStartDateTime) > new Date() ? (
                      <EventTime>
                        <Icon color="gray5">
                          <IconClockBolt size={20} />
                        </Icon>
                        <Time>
                          {new Intl.DateTimeFormat('hr', {
                            day: 'numeric',
                            month: 'narrow',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false,
                          }).format(new Date(lectureStartDateTime))}
                        </Time>
                      </EventTime>
                    ) : null}
                    {summary || documents.length > 0 ? (
                      <Icon color="blue">
                        <IconNotes size={20} />
                      </Icon>
                    ) : null}
                    {recordingId ? (
                      <Icon color="blue">{videoQualityIndicators(videoQuality)}</Icon>
                    ) : null}
                  </Icons>
                  <Tags>
                    {bonusModules?.find((b) => b.id === id) ? (
                      <Badge size="md" color={colors.lagoon} variant="light">
                        Bonus webinar
                      </Badge>
                    ) : null}
                    {tags.map((tag) => (
                      <Badge key={tag.id} size="md" color={colors.lagoon} variant="light">
                        {tag.tag}
                      </Badge>
                    ))}
                  </Tags>
                </Features>
                {!isCompact ? <Description>{description}</Description> : null}
              </Module>
            </ModuleWrap>
          )
        )}
    </Container>
  );
};

export { Modules };
